/** @jsxImportSource @emotion/react */
import {
  LargeText,
  MediumText,
  PAGE_COLUMN,
  PAGE_CONTENT,
  PAGE_TITLE,
} from "../styles/styles";
import * as React from "react";
import { Column, Row } from "../layout/flexbox";
import { IAppState } from "../core/redux/IAppState";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css } from "@emotion/react";
import { ValidationConfigurationTable } from "./ValidationConfigurationTable";
import { getError, getLoading } from "../core/redux/api/module";
import { ACTION_TYPE } from "../core/redux/actionType";
import {
  BLUE_CREEK,
  COOLER_GREY,
  DARK_BLUE_CREEK,
  LIGHT_GREY,
  RED_CREEK,
  WHITE,
} from "../styles/colors";
import { FADE_IN_ANIMATION, FADE_IN_KEYFRAMES } from "../styles/animations";
import styled from "@emotion/styled";
import { ApiError } from "../core/redux/api/apiError";
import { clearApiErrorAction } from "../core/redux/api/clearApiErrorAction";
import { showSnackbarAction } from "../snackbar/snackbarAction";
import { ROW_SPACING } from "../forms/styles";
import * as R from "ramda";
import { TooltipFC } from "../layout/TooltipFC";
import infoImg from "../assets/icons/info.svg";
import ConfirmationModal from "../forms/elements/ConfirmationModal";
import saveIcon from "../assets/icons/disk.svg";
import pencilIcon from "../assets/icons/pencil.svg";
import cancelIcon from "../assets/icons/xCircle.svg";
import historyIcon from "../assets/icons/history.svg";
import { BoxedErrorInverse } from "../forms/elements/boxedMessage/boxedMessageStyles";
import { getResource } from "../language/resources/getResource";
import {
  AUTH0_USER_PREFIX,
  EditableEffectiveDateRangeAccessRule,
  NUMBER_OF_HISTORY_EVENT_ROWS,
} from "./models";
import { getEffectiveDateRangeHistoryAction } from "./getEffectiveDateRangeHistoryAction";
import { getEditableEffectiveDateRangeRulesAction } from "./getEditableEffectiveDateRangeRulesAction";
import {
  buildValidationConfigurationUpdateRequest,
  effectiveDateRangeDatesChanged,
  effectiveDateRangeRuleUniqueKey,
} from "./module";
import { useEffectiveDateRangeValidation } from "./useEffectiveDateRangeValidation";
import { updateEditableEffectiveDateRangeRulesAction } from "./updateEditableEffectiveDateRangeRulesAction";

const ACTIONS = [
  ACTION_TYPE.UPDATE_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_REQUEST,
] as string[];

const styles = {
  titleRow: css`
    display: flex;
    flex-direction: row;
    width: 100%;
  `,
  titleRowLeft: css`
    display: flex;
    flex-direction: row;
    width: 50%;
    justify-content: flex-start;
    gap: 10px;
  `,
  titleRowRight: css`
    display: flex;
    flex-direction: row;
    width: 50%;
    justify-content: flex-end;
    gap: 10px;
  `,
  editButton: css`
    position: sticky;
    position: -webkit-sticky;
    top: 70px;
  `,
  buttonImage: css`
    width: 15px;
    margin-right: 7px;
  `,
  historyButtonImage: css`
    width: 18px;
    margin-right: 7px;
    margin-top: -2px; /* align image with text */
  `,
  buttonFlex: css`
    display: flex;
    alignitems: center;
  `,
};

const validationButton = css`
  text-align: center;
  text-decoration: none;
  ${MediumText};
  animation: 1s ${FADE_IN_KEYFRAMES};
  cursor: pointer;

  background-color: ${WHITE};
  border: solid 1px ${BLUE_CREEK};
  padding: 14px 31px;
  color: ${BLUE_CREEK};

  &:hover {
    background-color: ${LIGHT_GREY};
    color: ${DARK_BLUE_CREEK};
  }

  &:disabled {
    background-color: ${LIGHT_GREY};
    border: solid 1px ${COOLER_GREY};
    color: ${BLUE_CREEK};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ErrorPlaceholder = styled("div")`
  margin-bottom: 5px;
  min-height: 5px;
`;

const ErrorText = styled("span")`
  ${FADE_IN_ANIMATION};
  color: ${RED_CREEK};
  ${LargeText};
  margin-top: 10px;
  min-height: 20px;
  margin-bottom: 5px;
  max-width: 800px;
`;

const infoStyle = css`
  width: 20px;
  height: 20px;
  margin-bottom: -11px; /* align image with text */
  margin-left: 5px;
  pointer-events: initial;
`;

export const ValidationConfigurationPage = () => {
  const dispatch = useDispatch();

  const selectedResort = useSelector(
    (state: IAppState) => state.selectedResort,
  );

  const userState = useSelector((state: IAppState) => state.userState);

  const resortConfig = useSelector(
    (state: IAppState) => state.resortToolsConfig,
  );

  const effectiveDateRangeRules = useSelector(
    (state: IAppState) => state.editableEffectiveDateRangeAccessRules,
  );

  const isLoading = useSelector(
    (state: IAppState) =>
      !!getLoading(
        state,
        selectedResort?.resortAbbreviation,
        ACTION_TYPE.GET_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_REQUEST,
      ),
  );

  const errorState = useSelector((state: IAppState) => state.error);

  const isSaving = useSelector(
    (state: IAppState) =>
      !!getLoading(
        state,
        ACTION_TYPE.UPDATE_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_REQUEST,
        ACTION_TYPE.UPDATE_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_REQUEST,
      ),
  );

  const resources = useSelector((state: IAppState) => state.locale);

  const effectiveDateRangeHistory = useSelector(
    (state: IAppState) => state.effectiveDateRangeHistory,
  );

  const isLoadingHistory = useSelector(
    (state: IAppState) =>
      !!getLoading(
        state,
        ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_REQUEST,
        ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_REQUEST,
      ),
  );

  const historyError = useSelector((state: IAppState) =>
    getError(
      state,
      ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_REQUEST,
      ACTION_TYPE.GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_REQUEST,
    ),
  );

  const enableChangeHistory = useSelector(
    (state: IAppState) => state.resortToolsConfig.enableChangeHistory,
  );

  const users = useSelector((state: IAppState) => state.users);

  const isLoadingUsers = useSelector(
    (state: IAppState) =>
      !!getLoading(
        state,
        ACTION_TYPE.GET_USERS_REQUEST,
        ACTION_TYPE.GET_USERS_REQUEST,
      ),
  );

  /** Local State */
  const [configurations, setConfigurations] = useState(
    effectiveDateRangeRules?.map((c) => ({ ...c })),
  );

  // Keep original configurations in state to compare against for changes, and to reset to on cancel.
  const [originalConfigurations, setOriginalConfigurations] = useState<
    EditableEffectiveDateRangeAccessRule[] | null
  >(null);

  const [isEditing, setIsEditing] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState(false);

  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [ruleUpdatesToSave, setRuleUpdatesToSave] = useState<
    EditableEffectiveDateRangeAccessRule[] | undefined
  >(undefined);

  /** Derived State and memoized values */
  const validation = useEffectiveDateRangeValidation(configurations || []);

  const hasValidationErrors = React.useMemo(() => {
    return Object.keys(validation.raw.errors).length > 0;
  }, [validation]);

  const saveErrors = useMemo(() => {
    return errorState
      .filter((key, action) => ACTIONS.includes(action.actionType))
      .getValues();
  }, [errorState]);

  const fulfillmentSeasonIdsKey = useMemo(() => {
    return Array.from(
      new Set(effectiveDateRangeRules?.map((m) => m.fulfillmentSeasonId) ?? []),
    )
      .sort((a, b) => a - b)
      .join(",");
  }, [effectiveDateRangeRules]);

  /** Callbacks */
  const sortRules = useCallback(
    (rules: EditableEffectiveDateRangeAccessRule[]) => {
      // Default sort by product (ascending) then effectiveDate (descending)
      return rules
        .map((c) => ({ ...c }))
        .sort((a, b) => {
          const productCompare = a.product.localeCompare(b.product);
          if (productCompare !== 0) return productCompare;
          return (
            new Date(b.effectiveDate).getTime() -
            new Date(a.effectiveDate).getTime()
          );
        });
    },
    [],
  );

  const clearError = useCallback(
    (error: ApiError) =>
      dispatch(clearApiErrorAction(error.requestId, error.actionType)),
    [dispatch],
  );

  const showError = useCallback(
    (errorMsg: string) => dispatch(showSnackbarAction(errorMsg, true)),
    [dispatch],
  );

  const startEditing = useCallback(() => {
    setOriginalConfigurations(configurations?.map((c) => ({ ...c })));
    setConfigurations(configurations?.map((c) => ({ ...c })));
    setIsEditing(true);
  }, [configurations]);

  const resetLocalEdits = useCallback(() => {
    if (originalConfigurations) {
      setConfigurations(originalConfigurations);
    }

    setOriginalConfigurations(null);
    setIsEditing(false);
  }, [originalConfigurations]);

  const refreshChangeHistory = useCallback(() => {
    if (showChangeHistory && fulfillmentSeasonIdsKey) {
      const fulfillmentSeasonIds = fulfillmentSeasonIdsKey
        .split(",")
        .map(Number);
      dispatch(
        getEffectiveDateRangeHistoryAction(
          selectedResort.resortAbbreviation,
          NUMBER_OF_HISTORY_EVENT_ROWS,
          fulfillmentSeasonIds,
        ),
      );
    }
  }, [
    showChangeHistory,
    fulfillmentSeasonIdsKey,
    selectedResort.resortAbbreviation,
    dispatch,
  ]);

  const cancelEdits = useCallback(() => {
    resetLocalEdits();
    setError(false);

    dismissModal();
  }, [resetLocalEdits]);

  const getChangedEffectiveDateRangeRules =
    useCallback((): EditableEffectiveDateRangeAccessRule[] => {
      if (!originalConfigurations || !configurations) return [];

      const originalByKey = new Map(
        originalConfigurations.map((m) => [
          effectiveDateRangeRuleUniqueKey(m),
          m,
        ]),
      );

      const updates: EditableEffectiveDateRangeAccessRule[] = [];

      configurations.forEach((current) => {
        const key = effectiveDateRangeRuleUniqueKey(current);
        const original = originalByKey.get(key);

        if (!original) return;

        if (effectiveDateRangeDatesChanged(original, current)) {
          updates.push({
            liftAccessRuleId: current.liftAccessRuleId,
            entitlementTypeId: current.entitlementTypeId,
            fulfillmentSeasonId: current.fulfillmentSeasonId,
            resortId: current.resortId,
            product: current.product,
            season: current.season,
            fulfillmentSeason: current.fulfillmentSeason,
            effectiveDate: current.effectiveDate,
            expirationDate: current.expirationDate,
            effectiveTimeSlotStart: current.effectiveTimeSlotStart,
            effectiveTimeSlotEnd: current.effectiveTimeSlotEnd,
          });
        }
      });

      return updates;
    }, [configurations, originalConfigurations]);

  const hasChanges = React.useMemo(() => {
    return getChangedEffectiveDateRangeRules().length > 0;
  }, [getChangedEffectiveDateRangeRules]);

  const onChangeHistoryClick = () => {
    setShowChangeHistory(!showChangeHistory);
  };

  const onTooltipClick = (event) => event.preventDefault();

  /** Effects */
  useEffect(() => {
    refreshChangeHistory();
  }, [
    showChangeHistory,
    fulfillmentSeasonIdsKey,
    selectedResort.resortAbbreviation,
    refreshChangeHistory,
  ]);

  useEffect(() => {
    if (historyError) {
      clearError(historyError);
      showError(
        getResource(
          resources,
          "GET_EFFECTIVE_DATE_RANGE_RULE_HISTORY_ACTION_ERROR",
        ),
      );
    }
  }, [clearError, historyError, resources, showError]);

  useEffect(() => {
    const resortAbbr = selectedResort?.resortAbbreviation;

    if (!resortAbbr) {
      setCanEdit(false);
      return;
    }

    setIsEditing(false);

    dispatch(getEditableEffectiveDateRangeRulesAction(resortAbbr));

    setConfigurations([]);
    setOriginalConfigurations(null);
  }, [dispatch, selectedResort?.resortAbbreviation]);

  useEffect(() => {
    if (!isEditing && effectiveDateRangeRules) {
      setConfigurations(sortRules(effectiveDateRangeRules));
    }
  }, [effectiveDateRangeRules, isEditing, sortRules]);

  useEffect(() => {
    if (
      R.includes(userState.role, resortConfig!.manageValidationConfiguration) &&
      effectiveDateRangeRules?.length > 0
    ) {
      setCanEdit(true);
    }
  }, [userState.role, effectiveDateRangeRules, resortConfig]);

  useEffect(() => {
    if (saveErrors.length > 0) {
      saveErrors.forEach(clearError);
      showError(
        getResource(resources, "VALIDATION_CONFIG_FAILED_NETWORK_REQUEST"),
      );
    }
  }, [clearError, resources, saveErrors, showError]);

  const wasSaving = useRef(false);

  useEffect(() => {
    if (wasSaving.current && !isSaving && saveErrors.length === 0) {
      setConfigurations(sortRules(effectiveDateRangeRules ?? []));
      setOriginalConfigurations(null);
      setIsEditing(false);
      setRuleUpdatesToSave(undefined);
      refreshChangeHistory();
    }
    wasSaving.current = isSaving;
  }, [
    effectiveDateRangeRules,
    isSaving,
    saveErrors.length,
    sortRules,
    refreshChangeHistory,
  ]);

  useEffect(() => {
    // Map users from auth0 to auth0 username or user loading or user not found
    const mapHistoryUsers = effectiveDateRangeHistory?.map((history) => {
      const details = history.historyEventDetails.map((detail) => {
        if (detail.modifiedBy.startsWith(AUTH0_USER_PREFIX)) {
          // if no users - they may not be loaded yet
          if (!users || users.length === 0) {
            detail.modifiedByName = getResource(
              resources,
              "LIFT_ACCESS_HISTORY_USER_LOADING",
            );
          } else {
            detail.modifiedByName =
              users.find((user) => user.userId === detail.modifiedBy)
                ?.userName ||
              getResource(resources, "LIFT_ACCESS_HISTORY_USER_NOT_FOUND");
          }
        } else {
          // User not an Auth0 user
          detail.modifiedByName = detail.modifiedBy;
        }
        return detail;
      });
      history.historyEventDetails = details;
      return history;
    });
    R.assoc("effectiveDateRangeHistory", mapHistoryUsers);
  }, [users, effectiveDateRangeHistory, resources]);

  useEffect(() => {
    if (!hasValidationErrors) {
      setError(false);
    }
  }, [hasValidationErrors]);

  const handleSave = () => {
    if (hasValidationErrors) {
      setError(true);
      return;
    }

    const changedRules = getChangedEffectiveDateRangeRules();

    if (changedRules.length > 0) {
      setRuleUpdatesToSave(changedRules);
      setShowConfirmationModal(true);
    } else {
      // No changes
      setIsEditing(false);
      setOriginalConfigurations(null);
    }
  };

  const saveConfigurations = () => {
    dismissModal();

    if (ruleUpdatesToSave && ruleUpdatesToSave?.length > 0) {
      const request = buildValidationConfigurationUpdateRequest(
        ruleUpdatesToSave,
        selectedResort.resortAbbreviation,
        userState.userName,
        userState.userId,
      );
      dispatch(updateEditableEffectiveDateRangeRulesAction(request));
    } else {
      setIsEditing(false);
      setOriginalConfigurations(null);
    }
  };

  useEffect(() => {
    return () => {
      if (isEditing) {
        resetLocalEdits();
      }
    };
  }, [isEditing, resetLocalEdits]);

  const dismissModal = () => setShowConfirmationModal(false);

  return (
    <Column rootCss={PAGE_COLUMN}>
      <div css={styles.titleRow}>
        <div css={styles.titleRowLeft}>
          <span css={PAGE_TITLE}>
            {getResource(resources, "VALIDATION_CONFIG_TITLE")}
          </span>
          <TooltipFC
            content={
              <span>
                {getResource(resources, "VALIDATION_CONFIGURATION_PAGE_INFO")}
              </span>
            }
            children={
              <img
                alt="ⓘ"
                src={infoImg}
                css={infoStyle}
                onClick={onTooltipClick}
              />
            }
          />
        </div>

        <div css={styles.titleRowRight}>
          <Column>
            {canEdit && (
              <Row
                justifyContent="flex-end"
                rootCss={css`
                  padding-top: 25px;
                  padding-bottom: 1px;
                  ${ROW_SPACING};
                `}
              >
                {isEditing ? (
                  <button css={validationButton} onClick={cancelEdits}>
                    <div css={styles.buttonFlex}>
                      <img css={styles.buttonImage} src={cancelIcon} alt="" />
                      <span>
                        {getResource(
                          resources,
                          "VALIDATION_CONFIG_CANCEL_EDITS",
                        )}
                      </span>
                    </div>
                  </button>
                ) : enableChangeHistory ? (
                  <button css={validationButton} onClick={onChangeHistoryClick}>
                    <div css={styles.buttonFlex}>
                      <img
                        css={styles.historyButtonImage}
                        src={historyIcon}
                        alt=""
                      />
                      <span>
                        {showChangeHistory
                          ? getResource(
                              resources,
                              "VALIDATION_CONFIG_HIDE_HISTORY",
                            )
                          : getResource(
                              resources,
                              "VALIDATION_CONFIG_SHOW_HISTORY",
                            )}
                      </span>
                    </div>
                  </button>
                ) : (
                  ""
                )}
                {!isEditing ? (
                  <button css={validationButton} onClick={startEditing}>
                    <div css={styles.buttonFlex}>
                      <img css={styles.buttonImage} src={pencilIcon} alt="" />
                      <span>
                        {getResource(
                          resources,
                          "VALIDATION_CONFIG_EDIT_PRODUCTS",
                        )}
                      </span>
                    </div>
                  </button>
                ) : (
                  <button
                    css={validationButton}
                    onClick={handleSave}
                    disabled={!hasChanges}
                  >
                    <div css={styles.buttonFlex}>
                      <img css={styles.buttonImage} src={saveIcon} alt="" />
                      <span>
                        {getResource(
                          resources,
                          "VALIDATION_CONFIG_SAVE_PRODUCTS",
                        )}
                      </span>
                    </div>
                  </button>
                )}
              </Row>
            )}
            {error ? (
              <ErrorText id="validation-configuration-error-text">
                {getResource(resources, "VALIDATION_CONFIG_UNABLE_TO_SAVE")}
              </ErrorText>
            ) : (
              <ErrorPlaceholder />
            )}
          </Column>
        </div>
      </div>

      <div css={PAGE_CONTENT}>
        <ValidationConfigurationTable
          data={configurations}
          setConfigurations={setConfigurations}
          locked={!isEditing}
          isLoadingConfigurations={isLoading}
          isLoadingHistory={isLoadingHistory || isLoadingUsers}
          showChangeHistory={showChangeHistory}
          changeHistoryData={effectiveDateRangeHistory}
        />
      </div>
      {showConfirmationModal && (
        <ConfirmationModal
          resources={resources}
          title={getResource(resources, "VALIDATION_CONFIG_SAVE")}
          icon={saveIcon}
          message={getResource(
            resources,
            "VALIDATION_CONFIG_SAVE_PRODUCTS_CONFIRMATION",
          )}
          styledMessage={getResource(
            resources,
            "VALIDATION_CONFIG_DATA_CHANGE_WARNING",
          )}
          style={BoxedErrorInverse}
          positiveButtonText={getResource(resources, "VALIDATION_CONFIG_SAVE")}
          negativeButtonText={getResource(
            resources,
            "VALIDATION_CONFIG_CANCEL",
          )}
          onPositiveButtonClick={saveConfigurations}
          onNegativeButtonClick={cancelEdits}
          onDismiss={dismissModal}
        />
      )}
    </Column>
  );
};
